/**
 * Typed state surfaces for FmOperationalWorkspaceRoot.
 *
 * Every non-ready state is a designed surface with its own tone, copy and
 * resolving control — not one grey sentence in a dead band
 * Error and blocker surfaces render IN PLACE at the
 * scope that failed, so every region with served data keeps rendering it.
 */
import React from 'react';
import { FmBanner } from './FmBanner';
import { FmButton } from './FmButton';
import { FmEmpty, FmSpinner } from './FmFeedback';
import { FmUnlockPreview } from './FmUnlockPreview';
import type {
  FmOperationalActionDescriptor,
  FmOperationalPaidBoundaryDescriptor,
  FmOperationalWorkspaceDescriptor,
} from './FmOperationalWorkspaceRoot.types';
import {
  operationalControl,
  regionStateKind,
  scopedSurfaceRendersOnStage,
} from './FmOperationalWorkspaceStatus';

export type OperationalActionActivate = (
  action: FmOperationalActionDescriptor,
  trigger: HTMLButtonElement,
) => void;

/**
 * A control that resolves the surface it sits in. The validator guarantees it
 * is declared and `availableWhenUnavailable`, so it stays usable while the
 * state that produced it inhibits the ordinary toolbar.
 */
export function StateAction({
  descriptor,
  actionId,
  onActivate,
}: {
  descriptor: FmOperationalWorkspaceDescriptor;
  actionId: string;
  onActivate: OperationalActionActivate;
}): React.ReactElement | null {
  const action = operationalControl(descriptor, actionId);
  if (!action) return null;
  return (
    <FmButton
      variant={action.variant ?? 'primary'}
      size={action.size}
      danger={action.tone === 'destructive'}
      disabled={action.disabled}
      title={action.disabledReason}
      data-tone={action.tone}
      onClick={(event) => onActivate(action, event.currentTarget)}
      data-testid={`operational-action-${action.id}`}
    >
      {action.label}
    </FmButton>
  );
}

/**
 * The shared previewable paid boundary (gap G5), rendered by the SAME adapter
 * FmFormFlowPage uses. The withheld capability is named, priced, and one click
 * from its purchase path — never a greyed control with the reason hidden in a
 * `title` attribute.
 */
export function PaidBoundarySurface({
  descriptor,
  boundaries,
  onActivate,
  leading = false,
}: {
  descriptor: FmOperationalWorkspaceDescriptor;
  boundaries: readonly FmOperationalPaidBoundaryDescriptor[];
  onActivate: OperationalActionActivate;
  /** A stage-leading boundary sits directly in the body, so it takes the
      content rail and gutter itself; a region boundary inherits its region's. */
  leading?: boolean;
}): React.ReactElement | null {
  if (!boundaries.length) return null;
  const cards = (
    <>
      {boundaries.map((boundary) => (
        <FmUnlockPreview
          key={boundary.id}
          preview={boundary.preview}
          onUnlock={() => {
            const action = operationalControl(descriptor, boundary.preview.unlockActionId);
            /* The validator guarantees the control exists and is usable, so the
               card cannot be a dead end; the trigger is the card's own CTA. */
            if (action) onActivate(action, document.activeElement as HTMLButtonElement);
          }}
          data-testid={`operational-paid-boundary-${boundary.id}`}
        />
      ))}
    </>
  );
  return leading ? <div className="fm-operational-workspace__paid-boundary">{cards}</div> : cards;
}

function stateTestId(
  descriptor: FmOperationalWorkspaceDescriptor,
  workspaceTestId: string,
): string {
  return `${workspaceTestId}-state-${descriptor.state}`;
}

function BlockerSurface({
  descriptor,
  onActivate,
  testId,
}: {
  descriptor: Extract<FmOperationalWorkspaceDescriptor, { state: 'blocked' }>;
  onActivate: OperationalActionActivate;
  testId: string;
}): React.ReactElement {
  return (
    <FmBanner
      tone="warning"
      title={descriptor.blocked.name}
      description={descriptor.blocked.reason}
      action={
        <StateAction
          descriptor={descriptor}
          actionId={descriptor.blocked.resolvingActionId}
          onActivate={onActivate}
        />
      }
      data-testid={testId}
    />
  );
}

function ErrorSurface({
  descriptor,
  onActivate,
  testId,
}: {
  descriptor: Extract<FmOperationalWorkspaceDescriptor, { state: 'error' }>;
  onActivate: OperationalActionActivate;
  testId: string;
}): React.ReactElement {
  return (
    <FmBanner
      tone="error"
      title={descriptor.error.scopeLabel}
      description={descriptor.error.description}
      action={
        <StateAction
          descriptor={descriptor}
          actionId={descriptor.error.retryActionId}
          onActivate={onActivate}
        />
      }
      data-testid={testId}
    />
  );
}

/**
 * The surface that leads the stage body. Loading and a whole-workspace failure
 * replace the regions; empty, stale, and stage/workspace-scoped blockers lead
 * them while every region keeps rendering.
 */
export function WorkspaceLeadingStateSurface({
  descriptor,
  workspaceTestId,
  activeStageId,
  onActivate,
}: {
  descriptor: FmOperationalWorkspaceDescriptor;
  workspaceTestId: string;
  activeStageId: string;
  onActivate: OperationalActionActivate;
}): React.ReactElement | null {
  if (descriptor.state === 'ready') return null;
  const testId = stateTestId(descriptor, workspaceTestId);
  if (descriptor.state === 'loading') {
    return (
      <output
        className="fm-operational-workspace__state"
        data-kind="loading"
        aria-label={descriptor.stateLabel}
        data-testid={testId}
      >
        <FmSpinner />
      </output>
    );
  }
  if (descriptor.state === 'empty') {
    return (
      <output className="fm-operational-workspace__state" data-kind="empty" data-testid={testId}>
        <FmEmpty
          description={descriptor.empty.description}
          action={
            <StateAction
              descriptor={descriptor}
              actionId={descriptor.empty.primaryActionId}
              onActivate={onActivate}
            />
          }
        />
      </output>
    );
  }
  if (descriptor.state === 'stale') {
    return (
      <div className="fm-operational-workspace__state-notice" data-testid={testId}>
        <FmBanner
          tone="warning"
          title={descriptor.stale.name}
          description={descriptor.stale.description}
          action={
            <StateAction
              descriptor={descriptor}
              actionId={descriptor.stale.refreshActionId}
              onActivate={onActivate}
            />
          }
        />
      </div>
    );
  }
  /* A scoped surface leads the body whenever the stage the owner is looking at
     does not render it in place — otherwise navigating away leaves an inhibited
     workspace with no visible cause and no reachable resolving action. */
  if (scopedSurfaceRendersOnStage(descriptor, activeStageId)) return null;
  if (descriptor.state === 'blocked') {
    return (
      <div className="fm-operational-workspace__state-notice">
        <BlockerSurface descriptor={descriptor} onActivate={onActivate} testId={testId} />
      </div>
    );
  }
  return (
    <div className="fm-operational-workspace__state-notice">
      <ErrorSurface descriptor={descriptor} onActivate={onActivate} testId={testId} />
    </div>
  );
}

/**
 * The in-place surface a single region owns when it is the failing or blocked
 * scope. `null` means the region renders its served content.
 */
export function RegionStateSurface({
  descriptor,
  regionId,
  workspaceTestId,
  onActivate,
}: {
  descriptor: FmOperationalWorkspaceDescriptor;
  regionId: string;
  workspaceTestId: string;
  onActivate: OperationalActionActivate;
}): React.ReactElement | null {
  const kind = regionStateKind(descriptor, regionId);
  if (kind === 'error' && descriptor.state === 'error') {
    return (
      <ErrorSurface
        descriptor={descriptor}
        onActivate={onActivate}
        testId={`${workspaceTestId}-region-error-${regionId}`}
      />
    );
  }
  if (kind === 'blocked' && descriptor.state === 'blocked') {
    return (
      <BlockerSurface
        descriptor={descriptor}
        onActivate={onActivate}
        testId={`${workspaceTestId}-region-blocked-${regionId}`}
      />
    );
  }
  return null;
}
